import React, { useContext, useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import {
  Text,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import get from 'lodash/get';
import { Formik, Form } from 'formik';
import { navigate } from '@reach/router';
import * as Yup from 'yup';

import UserContext from '../../../contexts/UserContext';
import SpinnerContext from '../../../contexts/SpinnerContext';
import Input from '../../../components/Input';
import { FORCE_CANCEL_USER } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';

const ForceCancelUserSchema = Yup.object().shape({
  comment: Yup.string().required('Warum möchtest Du die Änderung durchführen?'),
});

const ForceCancelUser = ({ user: { id, firstName, lastName, email }, refetch }) => {
  const [alertOpen, setAlertOpen] = useState(false);
  const onAlertClose = () => setAlertOpen(false);
  const cancelRef = useRef();

  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useContext(SpinnerContext);
  const [forceCancelUserMutation] = useMutation(FORCE_CANCEL_USER);
  const toast = useCustomToast();

  const handleForceCancelSubmit = async ({ comment }) => {
    try {
      toggleSpinner(true);
      await forceCancelUserMutation({
        variables: {
          userId: id,
          internalUser: internalUser.id,
          comment,
        },
      });
      navigate('/users');
      toast({
        title: 'Erfolg!',
        description: 'Der User wurde gekündigt (AP Regeln überschrieben).',
        status: 'success',
      });
      setAlertOpen(false);
    } catch (error) {
      console.log(error);
      toast({
        description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
        status: 'error',
      });
    }
    toggleSpinner(false);
  };

  return (
    <>
      <Button variant="outline" colorScheme="orange" size="xs" onClick={() => setAlertOpen(true)}>
        User kündigen (AP Regeln überschreiben)
      </Button>

      <AlertDialog isOpen={alertOpen} leastDestructiveRef={cancelRef} onClose={onAlertClose}>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <Formik initialValues={{ comment: '' }} validationSchema={ForceCancelUserSchema} onSubmit={handleForceCancelSubmit}>
            <Form>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Bist Du sicher?
              </AlertDialogHeader>

              <AlertDialogBody>
                <Text mb="4">
                  Soll der Kunde {firstName} {lastName}, {email} gekündigt werden?
                </Text>
                <Text mb="4" fontSize="sm" color="orange.600" fontWeight="bold">
                  Achtung: Diese Aktion überschreibt die AccessPoint-Paketregeln. Der User wird gekündigt, auch wenn noch aktive AP-Pakete
                  vorhanden sind. Aktive DropFriends-Bestellungen blockieren weiterhin die Kündigung.
                </Text>
                <Text mb="4" fontSize="sm">
                  Die Daten des Users bleiben erhalten, aber die E-Mail wird verschleiert und der Account wird gesperrt.
                </Text>
                <Input name="comment" label="Kommentar" />
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onAlertClose}>
                  Abbrechen
                </Button>
                <Button colorScheme="orange" type="submit" ml={3}>
                  Kündigen (AP überschreiben)
                </Button>
              </AlertDialogFooter>
            </Form>
          </Formik>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ForceCancelUser;
